import { useMemo } from 'react';
import { useTheme } from '@/theme/useTheme';
import { PETAL_D } from '@/components/brand/geometry';
import { cn } from '@/components/ui';
import { contrastLevel, contrastRatio, parseRgb, toHex } from '@/lib/contrast';
import {
  colorGroups,
  contrastPairs,
  severities,
  severityLabels,
  toneLabels,
  toneRoles,
  tones,
  type Tone,
} from '../tokens';
import { useResolvedColors } from '../useResolvedColors';
import { Code, Section, SubTitle } from './Section';

// Статические строки классов — Tailwind генерирует только то, что встречает в исходниках.
const tonePill: Record<Tone, string> = {
  danger: 'bg-danger text-on-danger',
  warning: 'bg-warning text-on-warning',
  success: 'bg-success text-on-success',
  info: 'bg-info text-on-info',
  ai: 'bg-ai text-on-ai',
};
const toneSoft: Record<Tone, string> = {
  danger: 'border-danger-line bg-danger-soft text-danger-strong',
  warning: 'border-warning-line bg-warning-soft text-warning-strong',
  success: 'border-success-line bg-success-soft text-success-strong',
  info: 'border-info-line bg-info-soft text-info-strong',
  ai: 'border-ai-line bg-ai-soft text-ai-strong',
};
const toneText: Record<Tone, string> = {
  danger: 'text-danger-fg',
  warning: 'text-warning-fg',
  success: 'text-success-fg',
  info: 'text-info-fg',
  ai: 'text-ai-fg',
};
const sevPill = [
  'bg-sev-0 text-on-sev-0',
  'bg-sev-1 text-on-sev-1',
  'bg-sev-2 text-on-sev-2',
  'bg-sev-3 text-on-sev-3',
  'bg-sev-4 text-on-sev-4',
  'bg-sev-5 text-on-sev-5',
];
const sevSoft = [
  'bg-sev-0-soft',
  'bg-sev-1-soft',
  'bg-sev-2-soft',
  'bg-sev-3-soft',
  'bg-sev-4-soft',
  'bg-sev-5-soft',
];

/** Пластины веера сервисов на сцене знака: светлая (включён, активная) и притухшая (R15, spec-4 §4.2). */
const plateTones = [
  { name: 'brand-petal', fill: 'fill-petal', role: 'пластина: включён, активная' },
  {
    name: 'brand-petal-dim',
    fill: 'fill-petal-dim',
    role: 'притухшая пластина: = смесь petal 55 % и night, одна в обеих темах',
  },
] as const;

const levelBadge = {
  AA: 'bg-success text-on-success',
  'AA-large': 'bg-warning text-on-warning',
  fail: 'bg-danger text-on-danger',
} as const;

export function ColorsSection() {
  const { mode } = useTheme();

  const allNames = useMemo(() => {
    const names = new Set<string>();
    for (const g of colorGroups) for (const t of g.tokens) names.add(t.name);
    for (const t of tones) for (const r of toneRoles) names.add(r.startsWith('-') ? `${t}${r}` : `${r}${t}`);
    for (const n of severities) {
      names.add(`sev-${n}`);
      names.add(`on-sev-${n}`);
      names.add(`sev-${n}-soft`);
    }
    for (const p of contrastPairs) {
      names.add(p.fg);
      names.add(p.bg);
      if (p.base) names.add(p.base);
    }
    return [...names];
  }, []);

  const resolved = useResolvedColors(allNames, mode);
  const hex = (name: string) => (resolved[name] ? toHex(parseRgb(resolved[name])) : '…');

  const failures = contrastPairs.filter((p) => {
    const r =
      resolved[p.fg] && resolved[p.bg]
        ? contrastRatio(resolved[p.fg], resolved[p.bg], p.base ? resolved[p.base] : undefined)
        : 0;
    return r > 0 && r < p.min;
  }).length;

  return (
    <Section
      id="colors"
      title="Цвета"
      lead="Семантический ярус: страницы видят только эти имена. Значения берутся из CSS текущей темы, контраст считается живьём (R15): текст ≥ 4.5:1, иконки и кольцо фокуса ≥ 3:1; декоративные линии не нормируются. Переключи тему в шапке — таблица пересчитается."
    >
      {colorGroups.map((group) => (
        <div key={group.title} className="flex flex-col gap-3">
          <SubTitle>{group.title}</SubTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {group.tokens.map((t) => (
              <div key={t.name} className="rounded-md border border-line bg-card p-2">
                <div
                  className="h-12 rounded-sm border border-line"
                  style={{ background: `var(--${t.name})` }}
                  aria-hidden
                />
                <div className="mt-2 text-xs font-medium text-fg">{t.name}</div>
                <div className="text-2xs text-fg-subtle">{t.role}</div>
                <div className="font-mono text-2xs text-fg-subtle">{hex(t.name)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex flex-col gap-3">
        <SubTitle>Знак: пластины на сцене</SubTitle>
        <p className="text-sm text-fg-muted">
          Веер сервисов притухает цветом, а не прозрачностью: на перекрытых пластинах прозрачность даёт
          «рентген». <Code>petal-dim</Code> — заливка и контур притухших пластин (<Code>fill-petal-dim</Code>,{' '}
          <Code>stroke-petal-dim</Code>); сцена знака тёмная в обеих темах, контраст — к <Code>night</Code>.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {plateTones.map((t) => {
            const ready = resolved[t.name] && resolved['brand-night'];
            const ratio = ready ? contrastRatio(resolved[t.name], resolved['brand-night']) : 0;
            return (
              <div key={t.name} className="rounded-md border border-line bg-card p-2">
                <div className="grid h-12 place-items-center rounded-sm bg-night" aria-hidden>
                  <svg viewBox="-250 20 350 475" className="block size-10">
                    <path d={PETAL_D} className={t.fill} />
                  </svg>
                </div>
                <div className="mt-2 text-xs font-medium text-fg">{t.name.replace('brand-', '')}</div>
                <div className="text-2xs text-fg-subtle">{t.role}</div>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-2xs text-fg-subtle">{hex(t.name)}</span>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 font-mono text-2xs font-semibold',
                      ready
                        ? ratio >= 3
                          ? levelBadge[contrastLevel(ratio)]
                          : levelBadge.fail
                        : 'bg-muted text-fg-subtle',
                    )}
                  >
                    {ready ? `${ratio.toFixed(2)}:1 к night` : '…'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <SubTitle>Статусы: solid · on · fg · soft · strong · line</SubTitle>
        <p className="text-sm text-fg-muted">
          Плашки — solid + <Code>text-on-*</Code> (R9). Баннеры и подсветка строк — <Code>*-soft</Code> +{' '}
          <Code>*-strong</Code>. Цветной текст на холсте — <Code>*-fg</Code>.
        </p>
        <div className="overflow-x-auto rounded-lg border border-line bg-card">
          <table className="w-full text-left text-sm">
            <thead className="type-overline text-fg-subtle">
              <tr className="border-b border-line">
                <th className="px-4 py-3 font-semibold">тон</th>
                <th className="px-4 py-3 font-semibold">solid + on</th>
                <th className="px-4 py-3 font-semibold">fg на холсте</th>
                <th className="px-4 py-3 font-semibold">soft + strong + line</th>
              </tr>
            </thead>
            <tbody>
              {tones.map((t) => (
                <tr key={t} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-3 text-fg">{toneLabels[t]}</td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold',
                        tonePill[t],
                      )}
                    >
                      {t}
                    </span>
                    <span className="ml-2 font-mono text-2xs text-fg-subtle">{hex(t)}</span>
                  </td>
                  <td className={cn('px-4 py-3 font-medium', toneText[t])}>Текст {t}-fg</td>
                  <td className="px-4 py-3">
                    <div className={cn('rounded-md border px-3 py-2 text-xs', toneSoft[t])}>
                      Баннер: {t}-soft / {t}-strong
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <SubTitle>Шкала важности 0–5</SubTitle>
        <p className="text-sm text-fg-muted">
          Доменное семейство для событий, алертов и инцидентов. Тёплая порядковая шкала: чем хуже, тем темнее
          и краснее. Без фиолетового — он зарезервирован за AI (R10).
        </p>
        <div className="flex flex-wrap gap-2">
          {severities.map((n) => (
            <span
              key={n}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-2xs font-semibold',
                sevPill[n],
              )}
            >
              <span className={cn('size-1.5 rounded-full', sevSoft[n])} />
              {n} · {severityLabels[n]}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <SubTitle>Контраст пар</SubTitle>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-2xs font-semibold',
              failures === 0 ? 'bg-success text-on-success' : 'bg-danger text-on-danger',
            )}
          >
            {failures === 0 ? 'все пары проходят' : `провалов: ${failures}`}
          </span>
        </div>
        <div className="overflow-x-auto rounded-lg border border-line bg-card">
          <table className="w-full text-left text-sm">
            <thead className="type-overline text-fg-subtle">
              <tr className="border-b border-line">
                <th className="px-4 py-3 font-semibold">пара</th>
                <th className="px-4 py-3 font-semibold">текст</th>
                <th className="px-4 py-3 font-semibold">фон</th>
                <th className="px-4 py-3 font-semibold">образец</th>
                <th className="px-4 py-3 font-semibold">контраст</th>
                <th className="px-4 py-3 font-semibold">минимум</th>
              </tr>
            </thead>
            <tbody>
              {contrastPairs.map((p) => {
                const ready = resolved[p.fg] && resolved[p.bg];
                const ratio = ready
                  ? contrastRatio(resolved[p.fg], resolved[p.bg], p.base ? resolved[p.base] : undefined)
                  : 0;
                const level = contrastLevel(ratio);
                const ok = ratio >= p.min;
                return (
                  <tr key={p.label} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-2 text-fg">{p.label}</td>
                    <td className="px-4 py-2 font-mono text-2xs text-fg-subtle">{p.fg}</td>
                    <td className="px-4 py-2 font-mono text-2xs text-fg-subtle">{p.bg}</td>
                    <td className="px-4 py-2">
                      <span
                        className="inline-block rounded-sm px-2 py-1 text-xs font-medium"
                        style={{
                          color: `var(--${p.fg})`,
                          background: p.base
                            ? `linear-gradient(var(--${p.bg}), var(--${p.bg})), var(--${p.base})`
                            : `var(--${p.bg})`,
                        }}
                      >
                        Пример Aa
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 font-mono text-2xs font-semibold',
                          ready ? (ok ? levelBadge[level] : levelBadge.fail) : 'bg-muted text-fg-subtle',
                        )}
                      >
                        {ready ? `${ratio.toFixed(2)}:1 ${ok ? level : '✗'}` : '…'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-fg-subtle">{p.min}:1</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}
